/* eslint-disable react-hooks/exhaustive-deps */
import NewElectricityAgreementConfirmation from "components/electricity/NewElectricityAgreement/NewElectricityAgreementConfirmation";
import NewElectricityAgreementData from "components/electricity/NewElectricityAgreement/NewElectricityAgreementData";
import NewElectricityAgreementHeader from "components/electricity/NewElectricityAgreement/NewElectricityAgreementHeader";
import NewElectricityAgreementPackages, { getProductionPackageWithQuarterHourMeterType } from "components/electricity/NewElectricityAgreement/NewElectricityAgreementPackages";
import { NewElectricityAgreementSummary } from "components/electricity/NewElectricityAgreement/NewElectricityAgreementSummary";
import { NewElectricityAgreementTerms } from "components/electricity/NewElectricityAgreement/NewElectricityAgreementTerms";
import FormFooter from "components/FormFooter/FormFooter";
import { ElectricityAgreement } from "models/electricity/agreement";
import { MeteringPoint } from "models/electricity/metering-point";
import { ElectricityAgreementForm, ElectricityPackage, ElectricityPackageType, NewElectricityAgreementState } from "models/electricity/new-electricity-agreement";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { AuthenticationContext, CustomerPermission } from "services/context/authentication.context";
import ElectricityAgreementService from "services/electricity/electricity-agreement.service";
import { toast } from 'react-toastify';
import { isoDateFormat } from "common/dateFormatterConfig";
import { Spinner } from "react-bootstrap";
import MeteringPointService from "services/electricity/metering-point.service";
import { PageProps } from "models/page";
import { isEmailValidAndPresent, isValidCommaSeperatedEmail, validateBankAccountNumber } from "helpers/validators";
import { normalizeCommaSeparatedEmail } from "helpers";
import CustomerService from "services/customer.service";
import { useHistory } from "react-router-dom";

const PRODUCTION_CAPACITY_LIMIT = 15;

export interface AuthorizedMeteringPoint extends MeteringPoint {
    authorized? : boolean
}

export function NewElectricityAgreement(props: PageProps) {
    let history = useHistory();

    const [activeMenuOption, setActiveMenuOption] = useState(NewElectricityAgreementState.ACCEPT_TERMS);
    const [currentState, setCurrentState] = useState(NewElectricityAgreementState.ACCEPT_TERMS);
    const [eleringTermsAccepted, setEleringTermsAccepted] = useState(false);
    const [agreementTermsAccepted, setAgreementTermsAccepted] = useState(false);
    const [meteringPoints, setMeteringPoints] = useState<AuthorizedMeteringPoint[]>([]);
    const [consumptionPackages, setConsumptionPackages] = useState<ElectricityPackage[]>([]);
    const [productionPackages, setProductionPackages] = useState<ElectricityPackage[]>([]);
    const [productionPackage, setProductionPackage] = useState<ElectricityPackage>();
    const [consumptionPackage, setConsumptionPackage] = useState<ElectricityPackage>();
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState<ElectricityAgreementForm>({
        validated: false,
        sellCheck: false,
        consolidatedInvoice: true,
        eInvoice: false,
        electricityInvoiceRecipientEmailEnabled: false
    });

    const { state, dispatch, hasPermission } = useContext(AuthenticationContext);

    const meteringPointExceedsProductionCapacityLimit = 
        meteringPoints.some(mp => mp.productionCapacity > PRODUCTION_CAPACITY_LIMIT && mp.checked);

    const shouldRequestResaleAgreementOffer = form.sellCheck && meteringPointExceedsProductionCapacityLimit;
    const showResaleOfferOnPackages = form.sellCheck || !!state.changePackageAgreement?.productionPackage;

    const isJointInvoicePossible = false;

    const authorizationGivenToAllMeteringPoints = !!meteringPoints.length && meteringPoints.every(mp => mp.authorized);
    const authorizationGivenToSomeMeteringPoints = !!meteringPoints.length && meteringPoints.some(mp => mp.authorized);

    const checkIfAuthorizationWasGivenByRedirect = () => {
        const url = new URL(window.location.href);
        const redirectParam = url.searchParams.get('redirect');
        url.searchParams.delete('redirect');

        if (redirectParam === null || redirectParam === undefined) {
            return false;
        }

        return true;
    };

    useEffect(() => {
        props.onLoadValidatePage();

        return () => {
            if(state.changePackageAgreement)
                dispatch({
                    type: "setChangePackage",
                    value: null
                });
        }
    }, []);

    const fetchElectricityPackages = (nextState: NewElectricityAgreementState, meteringPoints: MeteringPoint[]): void => {
        setLoading(true);
                    
        if (shouldRequestResaleAgreementOffer) {
            ElectricityAgreementService.requestResaleAgreementOffer(state.customerId!, {
                contactPersonId: state.customer!.contactId,
                email: form.email!,
                phone: form.phone!,
                address: form.address!,
                bankAccountNumber: form.eInvoice ? form.bankAccountNumber : undefined,
                electricityInvoiceRecipientEmail: form.electricityInvoiceRecipientEmailEnabled ? form.electricityInvoiceRecipientEmail : undefined,
                meteringPoints: meteringPoints.map(mp => ({ id: mp.id, productionCapacity: mp.productionCapacity })),
                consolidatedInvoice: form.consolidatedInvoice,
                eInvoice: form.eInvoice
            }).then(() => {
                setActiveMenuOption(NewElectricityAgreementState.COMPLETED);
                setCurrentState(NewElectricityAgreementState.COMPLETED);
                setLoading(false)
                scrollTop()
            }).finally(() => setLoading(false));
        } else {
            ElectricityAgreementService.getElectricityPackages(
                state.customerId!, meteringPoints.map(mp => mp.eic)
            ).then(res => {
                const sortedPackages = res.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
                const packagesWithConsumptionType = res.filter(pckg => pckg.type === ElectricityPackageType.CONSUMPTION);
                const packagesWithProductionType = res.filter(pckg => pckg.type === ElectricityPackageType.PRODUCTION);

                setConsumptionPackages(packagesWithConsumptionType);
                setProductionPackages(packagesWithProductionType);
                setCurrentState(nextState);

                setActiveMenuOption(nextState);
                if (state.changePackageAgreement?.consumptionPackage.id) {
                    const matchingPackage = sortedPackages.find(p => p.id === state.changePackageAgreement?.consumptionPackage.id);

                    setConsumptionPackage(matchingPackage)
                    setProductionPackage(getProductionPackageWithQuarterHourMeterType(packagesWithProductionType));
                }
                setLoading(false)
                scrollTop()
            }).finally(() => setLoading(false));
        }
    }

    useEffect(() => {
        const agreement = state.changePackageAgreement;
        if (agreement) {
            setEleringTermsAccepted(true);
            setMeteringPoints(agreement.meteringPoints.map(mp => ({
                ...mp,
                checked: true
            })));

            setForm({
                ...form,
                customerName: agreement.customer.name,
                registryNumber: agreement.customer.registryNumber,
                email: agreement.contactPerson.email,
                phone: agreement.contactPerson.phone,
                address: !agreement.customer.city ? undefined : agreement.customer.address,
                city: agreement.customer.city,
                zip: agreement.customer.zip,
                county: agreement.customer.county,
                bankAccountNumber: agreement.customer.bankAccountNumber,
                eInvoice: agreement.customer.eInvoice,
                electricityInvoiceRecipientEmail: agreement.customer.electricityInvoiceRecipientEmail,
                electricityInvoiceRecipientEmailEnabled: !!agreement.customer.electricityInvoiceRecipientEmail
            });
            if (!!state.changePackageAgreement) {
                setCurrentState(NewElectricityAgreementState.CHOOSE_PACKAGE);
                setActiveMenuOption(NewElectricityAgreementState.CHOOSE_PACKAGE);
                fetchElectricityPackages(NewElectricityAgreementState.CHOOSE_PACKAGE, agreement.meteringPoints);
            } else {
                setConsumptionPackage(agreement.consumptionPackage);
                setProductionPackage(agreement.productionPackage)
                setCurrentState(NewElectricityAgreementState.CONFIRM);
                setActiveMenuOption(NewElectricityAgreementState.CONFIRM);
            }
        }
    }, [state.changePackageAgreement]);

    useEffect(() => {
        if(!isJointInvoicePossible && consumptionPackage?.jointInvoicePackage){
            setConsumptionPackage(undefined);
            setProductionPackage(undefined);
        }
    }, [isJointInvoicePossible])

    const onSelectPackages = (consumptionPackage: ElectricityPackage, productionPackage?: ElectricityPackage) => {
        setConsumptionPackage(consumptionPackage);
        setProductionPackage(productionPackage);
        onNext();
    }

    const scrollTop = () => {
        window.scrollTo(0,0)
    }

    const onNext = () => {
        const nextState = getNextState(activeMenuOption);
        if (nextState === NewElectricityAgreementState.DATA_AND_METERING_POINTS && authorizationGivenToSomeMeteringPoints) {
            setCurrentState(nextState);
            setActiveMenuOption(nextState);
            scrollTop()
        } else if (nextState === NewElectricityAgreementState.CHOOSE_PACKAGE) {
            if (validateForm() && meteringPoints.some(mp => mp.checked)) {
                fetchElectricityPackages(nextState, meteringPoints.filter(mp => mp.checked));        
            }
        } else if (nextState === NewElectricityAgreementState.CONFIRM) {
            setCurrentState(nextState);
            setActiveMenuOption(nextState);
            scrollTop()
        } else if (nextState === NewElectricityAgreementState.COMPLETED) {
            const startDate = moment(form.startDate!).startOf('day').add(2, 'hours').format(isoDateFormat);
            if (validateForm() && meteringPoints.some(mp => mp.checked)) {
                setLoading(true);
                if (state.changePackageAgreement) {
                    ElectricityAgreementService.changeElectricityAgreementPackage(
                        state.changePackageAgreement.customer.id,
                        state.changePackageAgreement.id,
                        {
                            consumptionPackageId: consumptionPackage!.id,
                            productionPackageId: state.changePackageAgreement?.productionPackage ? productionPackage!.id : null,
                            startDate,
                            consolidatedInvoice: form.consolidatedInvoice,
                            eInvoice: form.eInvoice,
                            email: form.email!,
                            phone: form.phone!,
                            address: form.address!,
                            city: form.city,
                            zip: form.zip,
                            county: form.county,
                            bankAccountNumber: form.eInvoice ? form.bankAccountNumber : undefined,
                            electricityInvoiceRecipientEmail: form.electricityInvoiceRecipientEmailEnabled ? normalizeCommaSeparatedEmail(form.electricityInvoiceRecipientEmail) : undefined, 
                        },
                        state.customer!.contactId
                    ).then(res => {
                        setCurrentState(nextState);
                        setActiveMenuOption(nextState);        

                        dispatch({
                            type: "setCustomer",
                            value: {
                                ...state.customer,
                                address: res.customer.address,
                                city: res.customer.city,
                                zip: res.customer.zip,
                                county: res.customer.county
                            }
                        });
                        dispatch({
                            type: "setChangePackage",
                            value: null
                        });
                        scrollTop()
                    }).finally(() => setLoading(false));
                } else {
                    const productionPackageId = (form.sellCheck && productionPackage) ? productionPackage.id : null;

                    ElectricityAgreementService.postElectricityAgreement(state.customerId!, {
                        contactPersonId: state.customer!.contactId,
                        email: form.email!,
                        phone: form.phone!,
                        address: form.address!,
                        city: form.city,
                        zip: form.zip,
                        county: form.county,
                        bankAccountNumber: form.eInvoice ? form.bankAccountNumber : undefined,
                        electricityInvoiceRecipientEmail: form.electricityInvoiceRecipientEmailEnabled ? normalizeCommaSeparatedEmail(form.electricityInvoiceRecipientEmail) : undefined,  
                        meteringPoints: meteringPoints.filter(mp => mp.checked).map(mp => ({
                            id: mp.id,
                            productionCapacity: mp.productionCapacity
                        })),
                        consumptionPackageId: consumptionPackage!.id,
                        productionPackageId,
                        startDate,
                        consolidatedInvoice: form.consolidatedInvoice,
                        eInvoice: form.eInvoice
                    }).then(res => {
                        finalizeNewAgreement(res, nextState);
                        scrollTop()
                    }).finally(() => setLoading(false));
                }

            }
        }
    }
    
    const authorizationGivenByRedirect = checkIfAuthorizationWasGivenByRedirect();

    const handlePreAuthorization = async () => {
        setLoading(true);

        try{
            const data = await MeteringPointService.getCustomerMeteringPoints(
                state.customerId!
            );

            const authResponse = await CustomerService.getEleringAuthorization(
                data.customerEic,
                data.meteringPoints.map(mp => mp.eic)
            );
            
            if(!authResponse.authorizations.length) return;

            let authorizedMeteringPoints;
            if(process.env.REACT_APP_IGNORE_ELERING_AUTHORIZATION === "true") {
                authorizedMeteringPoints = data.meteringPoints.map(meteringPoint => ({
                    ...meteringPoint,
                    authorized: true
                }))
            } else {
                authorizedMeteringPoints = data.meteringPoints.map(meteringPoint => ({
                    ...meteringPoint,
                    authorized: authResponse.authorizations.some(mp => mp.meteringPointEic === meteringPoint.eic && mp.authorized)
                }))
            }

            setMeteringPoints(
                authorizedMeteringPoints
            );

            const authGivenToAllMeteringPoints = authorizedMeteringPoints.every(mp => mp.authorized);

            if(authGivenToAllMeteringPoints){
                setEleringTermsAccepted(true);
                setCurrentState(NewElectricityAgreementState.DATA_AND_METERING_POINTS);
                setActiveMenuOption(NewElectricityAgreementState.DATA_AND_METERING_POINTS);
                scrollTop()
            }

        }catch(err){
            console.error(err);
            const e : any = err;

            if(authorizationGivenByRedirect){
                let message = e.message || '';
                if (e.message?.includes('NoCustomerAuthorization')) {
                    message = 'Puuduvad õigused Eleringist andmete pärimiseks';
                }
                toast(message, { type: 'error' });
            }
        }finally{
            setLoading(false);
        }
    }

    useEffect(() => {
        if (state.customer && !hasPermission(CustomerPermission.WEB_ADMIN)) history.push("/");

        if (currentState !== NewElectricityAgreementState.COMPLETED) {
            setForm({
                ...form,
                customerName: state.customer?.name,
                registryNumber: state.customer?.registryNumber,
                vatRegistryNumber: state.customer?.vatRegistryNumber,
                bankAccountNumber: state.customer?.bankAccountNumber,
                eInvoice: state.customer?.eInvoice,
                electricityInvoiceRecipientEmail: state.customer?.electricityInvoiceRecipientEmail,
                electricityInvoiceRecipientEmailEnabled: !!state.customer?.electricityInvoiceRecipientEmail,
                email: state.customer?.email,
                phone: state.customer?.phone,
                address: !state.customer?.city ? undefined : state.customer?.address,
                city: state.customer?.city,
                zip: state.customer?.zip,
                county: state.customer?.county,
                startDate: undefined
            });
            if (!state.changePackageAgreement) {
                setActiveMenuOption(NewElectricityAgreementState.ACCEPT_TERMS);
                setCurrentState(NewElectricityAgreementState.ACCEPT_TERMS);

                handlePreAuthorization();
            }
        }
    }, [state.customer]);

    if (!state.customer) {
        return (
            <div className='d-flex justify-content-center mt-5'>
                <Spinner animation="grow" variant="primary" />
            </div>
        );
    }

    const validateForm = () => {
        setForm({ ...form, validated: true });
        if (!isEmailValidAndPresent(form.email)) return false;
        if (!form.phone?.trim().length) return false;
        if (!form.address) return false;
        if (form.electricityInvoiceRecipientEmailEnabled && (!form.electricityInvoiceRecipientEmail || !isValidCommaSeperatedEmail(form.electricityInvoiceRecipientEmail))) {
            return false;
        }
        if(form.eInvoice){
            if(!form.bankAccountNumber){
                return false;
            }
            if(!validateBankAccountNumber(form.bankAccountNumber)){
                return false
            }
            return true;
        }
        return true;
    };

    const onPrevious = () => {
        const prevState = getPrevState(activeMenuOption);
        setCurrentState(prevState);
        setActiveMenuOption(prevState);
        scrollTop();
    }

    const finalizeNewAgreement = (agreement: ElectricityAgreement, nextState: NewElectricityAgreementState) => {
        setCurrentState(nextState);
        setActiveMenuOption(nextState);
        dispatch({
            type: "setCustomer",
            value: {
                ...state.customer,
                activeElectricityAgreementsCount: state.customer.activeElectricityAgreementsCount + 1,
                address: agreement.customer.address,
                city: agreement.customer.city,
                zip: agreement.customer.zip,
                county: agreement.customer.county
            }
        });
    }

    const onHeaderOptionChange = (nextState: NewElectricityAgreementState): void => {
        if (nextState === NewElectricityAgreementState.DATA_AND_METERING_POINTS ||
            nextState === NewElectricityAgreementState.ACCEPT_TERMS) {
            setCurrentState(nextState);
        }
        setActiveMenuOption(nextState);
    } 

    let nextButtonDisabled = false;
    if ((currentState === NewElectricityAgreementState.ACCEPT_TERMS && !eleringTermsAccepted) ||
        (currentState === NewElectricityAgreementState.CONFIRM && !agreementTermsAccepted) ||
        (currentState === NewElectricityAgreementState.DATA_AND_METERING_POINTS && !meteringPoints.some(mp => mp.checked))
    ) {
        nextButtonDisabled = true;
    }

    let nextLabelText = 'Edasi';
    if (currentState === NewElectricityAgreementState.CONFIRM && activeMenuOption === NewElectricityAgreementState.CONFIRM) {
        nextLabelText = state.changePackageAgreement ? 'Muuda pakett' : 'Sõlmi leping';
    }

    if (currentState === NewElectricityAgreementState.DATA_AND_METERING_POINTS && shouldRequestResaleAgreementOffer) {
        nextLabelText = 'Küsi pakkumist';
    }

    let previousLabelText = 'Tagasi';

    return (
        <div className="new-electricity-agreement">
            {currentState !== NewElectricityAgreementState.COMPLETED ?
                <NewElectricityAgreementHeader
                    activeItem={activeMenuOption}
                    setActiveItem={onHeaderOptionChange}
                    currentItem={currentState}
                    disabled={loading}
                    changingPackage={!!state.changePackageAgreement}
                    acceptingOffer={false}
                />: null}
            { activeMenuOption === NewElectricityAgreementState.ACCEPT_TERMS ?
                <NewElectricityAgreementTerms
                    termsAccepted={eleringTermsAccepted}
                    toggleTerms={() => setEleringTermsAccepted(!eleringTermsAccepted)}
                    authorizationGivenToAllMeteringPoints={authorizationGivenToAllMeteringPoints}
                    authorizationGivenToSomeMeteringPoints={authorizationGivenToSomeMeteringPoints}
                    isLoading={loading}
                /> : null }
            { activeMenuOption === NewElectricityAgreementState.DATA_AND_METERING_POINTS ?
                <NewElectricityAgreementData
                    isCompany={state.customer && !state.customer.isPerson && !state.customer.apartmentAssociation}
                    form={form}
                    setForm={setForm}
                    meteringPoints={meteringPoints.filter(meteringPoint => meteringPoint.authorized)}
                    setMeteringPoints={setMeteringPoints}
                /> : null }
            { activeMenuOption === NewElectricityAgreementState.CHOOSE_PACKAGE ?
                <NewElectricityAgreementPackages
                    consumptionPackages={consumptionPackages}
                    productionPackages={productionPackages}
                    showResaleOffer={showResaleOfferOnPackages}
                    selectPackages={onSelectPackages}
                    selectedPackage={consumptionPackage}
                    loading={loading}
                    isCompany={state.customer && !state.customer.isPerson && !state.customer.apartmentAssociation}
                /> : null }
            { activeMenuOption === NewElectricityAgreementState.CONFIRM ?
                <NewElectricityAgreementSummary
                    selectedPackage={consumptionPackage as ElectricityPackage}
                    productionPackage={productionPackage}
                    selectedMeteringPoints={meteringPoints.filter(mp => mp.checked)}
                    isCompany={state.customer && !state.customer.isPerson && !state.customer.apartmentAssociation}
                    form={form}
                    setForm={setForm}
                    onChangePackage={onPrevious}
                    termsAccepted={agreementTermsAccepted}
                    toggleTerms={() => setAgreementTermsAccepted(!agreementTermsAccepted)}
                /> : null }
            { activeMenuOption === NewElectricityAgreementState.COMPLETED ?
                <NewElectricityAgreementConfirmation
                    isResaleOffer={form.sellCheck}
                    isChangingPackage={!!state.changePackageAgreement}
                />: null}                
            {
                hasPermission(CustomerPermission.WEB_ADMIN) && (
                    <FormFooter
                        previous={onPrevious}
                        next={onNext}
                        nextButtonDisabled={nextButtonDisabled}
                        loading={loading}
                        showPrevious={
                            activeMenuOption !== NewElectricityAgreementState.ACCEPT_TERMS &&
                            currentState !== NewElectricityAgreementState.COMPLETED &&
                            (activeMenuOption !== NewElectricityAgreementState.CHOOSE_PACKAGE || !state.changePackageAgreement)
                        }
                        showNext={
                            currentState !== NewElectricityAgreementState.COMPLETED &&
                            ((currentState === NewElectricityAgreementState.ACCEPT_TERMS && authorizationGivenToSomeMeteringPoints) || currentState !== NewElectricityAgreementState.ACCEPT_TERMS) &&
                            (activeMenuOption !== NewElectricityAgreementState.CHOOSE_PACKAGE || !!consumptionPackage)
                        }
                        previousLabel={previousLabelText}
                        nextLabel={nextLabelText}
                     ></FormFooter>
                )
            }
        </div>
    )
}

const getPrevState = (currentState: NewElectricityAgreementState): NewElectricityAgreementState => {
    switch (currentState) {
        case NewElectricityAgreementState.CHOOSE_PACKAGE:
            return NewElectricityAgreementState.DATA_AND_METERING_POINTS;
        case NewElectricityAgreementState.CONFIRM:
            return NewElectricityAgreementState.CHOOSE_PACKAGE;
        case NewElectricityAgreementState.COMPLETED:
            return currentState;
        default: return NewElectricityAgreementState.ACCEPT_TERMS;
    }
}

const getNextState = (currentState: NewElectricityAgreementState): NewElectricityAgreementState => {
    switch (currentState) {
        case NewElectricityAgreementState.ACCEPT_TERMS:
            return NewElectricityAgreementState.DATA_AND_METERING_POINTS;
        case NewElectricityAgreementState.DATA_AND_METERING_POINTS:
            return NewElectricityAgreementState.CHOOSE_PACKAGE;
        case NewElectricityAgreementState.CHOOSE_PACKAGE:
            return NewElectricityAgreementState.CONFIRM;
        default: return NewElectricityAgreementState.COMPLETED;
    }
}